import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Form, Row, Col, Badge, Button, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './Admin.css';

const PointsReport = () => {
  const [transactions, setTransactions] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({
    period: 'all',
    type: 'all',
    location: 'all'
  });
  const [stats, setStats] = useState({
    totalPoints: 0,
    totalEarned: 0,
    totalSpent: 0,
    activeUsers: 0
  });

  const navigate = useNavigate();

  useEffect(() => {
    loadRealData();
  }, []);

  const loadRealData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Vérifier si l'utilisateur est connecté
      const token = localStorage.getItem('token') || localStorage.getItem('adminToken');

      if (!token) {
        setError('Veuillez vous connecter pour accéder aux données');
        setLoading(false);
        navigate('/admin-login');
        return;
      }

      console.log('🔍 Chargement des données réelles depuis l\'API admin...');

      try {
        // 1. Vérifier la santé de l'API
        const healthResponse = await api.get('/api/admin/health');
        console.log('✅ Health check:', healthResponse.data);

        // 2. Charger les clients depuis l'API admin
        console.log('🔍 Envoi requête /api/admin/clients...');
        const clientsResponse = await api.get('/api/admin/clients');

        // AJOUTEZ CE LOG IMPORTANT
        console.log('📊 RÉPONSE COMPLÈTE clients:', {
          status: clientsResponse.status,
          data: clientsResponse.data,
          success: clientsResponse.data.success,
          message: clientsResponse.data.message,
          clientsCount: clientsResponse.data.clients?.length,
          clientsSample: clientsResponse.data.clients?.slice(0, 2)
        });

        if (clientsResponse.data.success) {
          const clientsData = clientsResponse.data.clients || [];

          if (clientsData.length === 0) {
            console.log('⚠️ Aucun client dans la réponse');
            setError('Aucun client trouvé dans la base de données');
            setClients([]);
            setTransactions([]);
          } else {
            console.log(`✅ ${clientsData.length} clients reçus de l'API`);

            // Afficher le premier client pour debug
            if (clientsData[0]) {
              console.log('👤 Premier client reçu:', {
                id: clientsData[0]._id,
                name: clientsData[0].name,
                email: clientsData[0].email,
                points: clientsData[0].points,
                hasTransactions: !!clientsData[0].transactions,
                transactionsCount: clientsData[0].transactions?.length || 0
              });
            }

            setClients(clientsData);

            // Extraire les transactions des clients
            const allTransactions = extractTransactionsFromClients(clientsData);
            console.log(`📊 Transactions extraites: ${allTransactions.length}`);

            const transformedTransactions = transformTransactions(allTransactions);
            setTransactions(transformedTransactions);

            // Calculer les statistiques
            calculateStats(transformedTransactions, clientsData);

            console.log(`✅ Données chargées: ${clientsData.length} clients, ${transformedTransactions.length} transactions`);
          }
        } else {
          console.error('❌ API a retourné success: false', clientsResponse.data);
          setError('Erreur API: ' + (clientsResponse.data.message || 'Réponse invalide'));
        }

      } catch (apiError) {
        console.error('❌ Erreur API admin:', apiError.message);
        console.error('❌ Réponse:', apiError.response?.data);

        if (apiError.response?.status === 401) {
          setError('Session expirée. Veuillez vous reconnecter.');
          localStorage.removeItem('token');
          localStorage.removeItem('adminToken');
          navigate('/admin-login');
        } else if (apiError.response?.status === 403) {
          setError('Accès refusé. Droits administrateur requis.');
        } else {
          setError(`Erreur API: ${apiError.message}`);
        }
      }

    } catch (err) {
      console.error('❌ Erreur générale:', err);
      setError('Erreur lors du chargement des données');

    } finally {
      setLoading(false);
    }
  };

  // Fonction pour charger des données de test en attendant l'API réelle
  const loadTestData = async () => {
    try {
      // Données de test pour la démonstration
      const testClients = [
        {
          _id: '1',
          nom: 'Jean Dupont',
          email: 'jean.dupont@email.com',
          points: 1250,
          level: 'Gold',
          transactions: [
            {
              _id: 't1',
              type: 'earned',
              points: 100,
              description: 'Achat au comptoir',
              location: 'KDO+ PARIS',
              createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
            },
            {
              _id: 't2',
              type: 'spent',
              points: -250,
              description: 'Échange pour un café',
              location: 'KDO+ PARIS',
              createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString()
            }
          ]
        },
        {
          _id: '2',
          nom: 'Marie Curie',
          email: 'marie.curie@email.com',
          points: 4500,
          level: 'Platinum',
          transactions: [
            {
              _id: 't3',
              type: 'earned',
              points: 500,
              description: 'Achat en ligne',
              location: 'KDO+ ONLINE',
              createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
            }
          ]
        }
      ];

      const testTransactions = [
        {
          _id: 't1',
          clientId: '1',
          type: 'earned',
          points: 100,
          description: 'Achat au comptoir',
          location: 'KDO+ PARIS',
          createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          clientName: 'Jean Dupont',
          clientEmail: 'jean.dupont@email.com'
        },
        {
          _id: 't2',
          clientId: '1',
          type: 'spent',
          points: -250,
          description: 'Échange pour un café',
          location: 'KDO+ PARIS',
          createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
          clientName: 'Jean Dupont',
          clientEmail: 'jean.dupont@email.com'
        },
        {
          _id: 't3',
          clientId: '2',
          type: 'earned',
          points: 500,
          description: 'Achat en ligne',
          location: 'KDO+ ONLINE',
          createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
          clientName: 'Marie Curie',
          clientEmail: 'marie.curie@email.com'
        }
      ];

      setClients(testClients);
      setTransactions(testTransactions);

      // Calculer les statistiques de test
      const earned = testTransactions
        .filter(t => t.type === 'earned' || t.points > 0)
        .reduce((sum, t) => sum + Math.abs(t.points), 0);

      const spent = testTransactions
        .filter(t => t.type === 'spent' || t.points < 0)
        .reduce((sum, t) => sum + Math.abs(t.points), 0);

      const totalPoints = testClients.reduce((sum, c) => sum + (c.points || 0), 0);

      setStats({
        totalPoints,
        totalEarned: earned,
        totalSpent: spent,
        activeUsers: testClients.length
      });

      console.log('📊 Données de test chargées');

    } catch (err) {
      console.error('❌ Erreur chargement données test:', err);
      setError('Impossible de charger les données. Veuillez vérifier votre connexion.');
    }
  };

  const extractTransactionsFromClients = (clients) => {
    console.log('🔍 Extraction transactions depuis', clients.length, 'clients');

    const allTransactions = [];

    clients.forEach((client, index) => {
      console.log(`  Client ${index + 1}: ${client.name || client.email}`);

      if (client.transactions && Array.isArray(client.transactions)) {
        console.log(`    ${client.transactions.length} transactions trouvées`);

        client.transactions.forEach((transaction, tIndex) => {
          allTransactions.push({
            ...transaction,
            clientId: client._id,
            clientName: client.name || client.nom || 'Client',
            clientEmail: client.email
          });

          // Log de la première transaction pour debug
          if (tIndex === 0) {
            console.log('    Première transaction:', {
              type: transaction.type,
              points: transaction.points,
              description: transaction.description
            });
          }
        });
      } else {
        console.log(`    Pas de transactions ou pas un tableau`);
      }
    });

    console.log(`📊 Total transactions extraites: ${allTransactions.length}`);

    return allTransactions;
  };
  const transformTransactions = (transactions) => {
    return transactions.map(transaction => {
      const points = transaction.points || transaction.amount || 0;
      const type = transaction.type || (points > 0 ? 'earned' : 'spent');

      return {
        id: transaction._id || transaction.id || Math.random().toString(),
        clientId: transaction.clientId,
        clientName: transaction.clientName || 'Client',
        clientEmail: transaction.clientEmail || 'N/A',
        date: transaction.createdAt || transaction.date || new Date().toISOString(),
        type: type,
        points: points,
        description: transaction.description ||
          (type === 'earned' ? 'Points gagnés' :
            type === 'spent' ? 'Points dépensés' : 'Transaction'),
        billNumber: transaction.billNumber,
        rewardName: transaction.rewardName,
        voucherCode: transaction.voucherCode,
        location: transaction.location || 'KDO+',
        status: transaction.status || 'completed'
      };
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const calculateStats = (transactions, clients) => {
    const earned = transactions
      .filter(t => t.type === 'earned' || t.points > 0)
      .reduce((sum, t) => sum + Math.abs(t.points), 0);

    const spent = transactions
      .filter(t => t.type === 'spent' || t.points < 0)
      .reduce((sum, t) => sum + Math.abs(t.points), 0);

    const totalPoints = clients.reduce((sum, c) => sum + (c.points || 0), 0);

    setStats({
      totalPoints,
      totalEarned: earned,
      totalSpent: spent,
      activeUsers: clients.length
    });
  };

  const filterByPeriod = (transactionDate, period) => {
    const date = new Date(transactionDate);
    const now = new Date();

    switch (period) {
      case 'today':
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date >= today;

      case 'week':
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return date >= weekAgo;

      case 'month':
        const monthAgo = new Date();
        monthAgo.setDate(monthAgo.getDate() - 30);
        return date >= monthAgo;

      case 'all':
      default:
        return true;
    }
  };

  const filteredTransactions = transactions.filter(t => {
    if (filter.type !== 'all' && t.type !== filter.type) return false;
    if (filter.location !== 'all' && t.location !== filter.location) return false;
    if (filter.period !== 'all' && !filterByPeriod(t.date, filter.period)) return false;
    return true;
  });

  const locations = ['all', ...new Set(transactions.map(t => t.location).filter(Boolean))];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportToCSV = () => {
    if (filteredTransactions.length === 0) return;

    const headers = ['Date', 'Client', 'Email', 'Type', 'Points', 'Description', 'Localisation'];
    const csvData = filteredTransactions.map(t => [
      formatDate(t.date),
      `"${t.clientName}"`,
      t.clientEmail,
      t.type === 'earned' ? 'Gagné' : 'Dépensé',
      Math.abs(t.points),
      `"${t.description}"`,
      t.location
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `rapport-points-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRefresh = () => {
    loadRealData();
  };

  const handleLogin = () => {
    navigate('/admin-login');
  };

  const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
  const isAuthenticated = !!token;

  return (
    <Container className="admin-page">
      <div className="page-header">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 className="text-primary mb-2">📊 Rapports Points - KDO+</h1>
            <p className="text-muted">
              {transactions.length > 0 && transactions[0].clientEmail.includes('test')
                ? 'Données de démonstration'
                : 'Données réelles de la base de données'}
            </p>
          </div>
          {!isAuthenticated && (
            <Button variant="outline-primary" onClick={handleLogin}>
              🔐 Connexion Admin
            </Button>
          )}
        </div>
      </div>

      {error && (
        <Alert variant={error.includes('connecter') ? 'warning' : 'danger'} className="mb-4">
          <div className="d-flex align-items-center">
            <div className="me-3">⚠️</div>
            <div>
              <strong>{error.includes('connecter') ? 'Information' : 'Erreur'}:</strong> {error}
              {error.includes('connecter') && (
                <div className="mt-2">
                  <Button size="sm" variant="outline-primary" onClick={handleLogin}>
                    Se connecter
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Alert>
      )}

      <Row className="mb-4">
        <Col md={3}>
          <Card className="shadow-lg text-center h-100">
            <Card.Body>
              <div className="stat-icon mb-3" style={{ fontSize: '2rem', color: '#28a745' }}>
                ⬆️
              </div>
              <h3>{stats.totalEarned.toLocaleString()}</h3>
              <p className="text-muted mb-0">Points distribués</p>
              <small className="text-success">
                {transactions.filter(t => t.type === 'earned').length} distributions
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="shadow-lg text-center h-100">
            <Card.Body>
              <div className="stat-icon mb-3" style={{ fontSize: '2rem', color: '#ffc107' }}>
                ⬇️
              </div>
              <h3>{stats.totalSpent.toLocaleString()}</h3>
              <p className="text-muted mb-0">Points échangés</p>
              <small className="text-warning">
                {transactions.filter(t => t.type === 'spent').length} échanges
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="shadow-lg text-center h-100">
            <Card.Body>
              <div className="stat-icon mb-3" style={{ fontSize: '2rem', color: '#17a2b8' }}>
                💰
              </div>
              <h3>{stats.totalPoints.toLocaleString()}</h3>
              <p className="text-muted mb-0">Points en circulation</p>
              <small className="text-info">
                {stats.activeUsers} clients actifs
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="shadow-lg text-center h-100">
            <Card.Body>
              <div className="stat-icon mb-3" style={{ fontSize: '2rem', color: '#7B1FA2' }}>
                🔄
              </div>
              <h3>{transactions.length}</h3>
              <p className="text-muted mb-0">Transactions totales</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="shadow-lg mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h4>Transactions clients {transactions.length > 0 && transactions[0].clientEmail.includes('test') ? '(données de démonstration)' : '(données réelles)'}</h4>
              <small className="text-muted">
                {filteredTransactions.length} transactions affichées sur {transactions.length}
              </small>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                onClick={handleRefresh}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Chargement...
                  </>
                ) : (
                  '🔄 Actualiser'
                )}
              </Button>
              <Button
                variant="outline-success"
                onClick={exportToCSV}
                disabled={filteredTransactions.length === 0}
              >
                📥 Exporter CSV
              </Button>
            </div>
          </div>

          <Row className="mb-4">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Type de transaction</Form.Label>
                <Form.Select
                  value={filter.type}
                  onChange={(e) => setFilter({ ...filter, type: e.target.value })}
                  disabled={loading}
                >
                  <option value="all">Tous les types</option>
                  <option value="earned">Points gagnés</option>
                  <option value="spent">Points dépensés</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Point de vente</Form.Label>
                <Form.Select
                  value={filter.location}
                  onChange={(e) => setFilter({ ...filter, location: e.target.value })}
                  disabled={loading}
                >
                  {locations.map(loc => (
                    <option key={loc} value={loc}>
                      {loc === 'all' ? 'Tous les points de vente' : loc}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Période</Form.Label>
                <Form.Select
                  value={filter.period}
                  onChange={(e) => setFilter({ ...filter, period: e.target.value })}
                  disabled={loading}
                >
                  <option value="all">Toute période</option>
                  <option value="today">Aujourd'hui</option>
                  <option value="week">7 derniers jours</option>
                  <option value="month">30 derniers jours</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Chargement des données...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Client</th>
                      <th>Type</th>
                      <th>Points</th>
                      <th>Description</th>
                      <th>Point de vente</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.map(transaction => (
                      <tr key={transaction.id}>
                        <td>
                          <small>{formatDate(transaction.date)}</small>
                        </td>
                        <td>
                          <div className="fw-bold">{transaction.clientName}</div>
                          <small className="text-muted">{transaction.clientEmail}</small>
                        </td>
                        <td>
                          <Badge bg={
                            transaction.type === 'earned' ? "success" :
                              transaction.type === 'spent' ? "warning" : "secondary"
                          }>
                            {transaction.type === 'earned' ? "Gagné" :
                              transaction.type === 'spent' ? "Dépensé" : "Autre"}
                          </Badge>
                        </td>
                        <td className={transaction.type === 'earned' ? 'text-success' : 'text-danger'}>
                          <strong>
                            {transaction.type === 'earned' ? '+' : '-'}{Math.abs(transaction.points)}
                          </strong>
                        </td>
                        <td>
                          <div>{transaction.description}</div>
                          {transaction.billNumber && (
                            <small className="text-muted">Facture: {transaction.billNumber}</small>
                          )}
                          {transaction.rewardName && (
                            <small className="text-muted d-block">Récompense: {transaction.rewardName}</small>
                          )}
                          {transaction.voucherCode && (
                            <small className="text-muted d-block">Voucher: {transaction.voucherCode}</small>
                          )}
                        </td>
                        <td>
                          <Badge bg="light" text="dark">
                            {transaction.location}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {filteredTransactions.length === 0 && (
                <div className="text-center py-5">
                  <div style={{ fontSize: '4rem' }}>📭</div>
                  <h4 className="mt-3">Aucune transaction</h4>
                  <p className="text-muted">
                    {transactions.length === 0
                      ? "Aucune donnée disponible."
                      : "Aucune transaction ne correspond aux filtres sélectionnés."}
                  </p>
                  {transactions.length > 0 && (
                    <Button
                      variant="outline-primary"
                      onClick={() => setFilter({ period: 'all', type: 'all', location: 'all' })}
                      className="mt-3"
                    >
                      Réinitialiser les filtres
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {transactions.length > 0 && (
        <Row>
          <Col md={6}>
            <Card className="shadow-lg mb-4">
              <Card.Body>
                <h4 className="mb-4">📍 Activité par point de vente</h4>
                {locations.filter(l => l !== 'all').map(location => {
                  const locTransactions = filteredTransactions.filter(t => t.location === location);
                  const earned = locTransactions.filter(t => t.type === 'earned')
                    .reduce((sum, t) => sum + Math.abs(t.points), 0);
                  const spent = locTransactions.filter(t => t.type === 'spent')
                    .reduce((sum, t) => sum + Math.abs(t.points), 0);

                  if (locTransactions.length === 0) return null;

                  return (
                    <div key={location} className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <strong>{location}</strong>
                        <span>{locTransactions.length} transactions</span>
                      </div>
                      <div className="d-flex justify-content-between small text-muted mb-2">
                        <span>Distribués: +{earned.toLocaleString()}</span>
                        <span>Échangés: -{spent.toLocaleString()}</span>
                      </div>
                      <div className="progress" style={{ height: '10px' }}>
                        {earned + spent > 0 && (
                          <>
                            <div
                              className="progress-bar bg-success"
                              style={{ width: `${(earned / (earned + spent)) * 100}%` }}
                            ></div>
                            <div
                              className="progress-bar bg-warning"
                              style={{ width: `${(spent / (earned + spent)) * 100}%` }}
                            ></div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="shadow-lg mb-4">
              <Card.Body>
                <h4 className="mb-4">👥 Top clients</h4>
                <div className="clients-list">
                  {clients
                    .sort((a, b) => (b.points || 0) - (a.points || 0))
                    .slice(0, 5)
                    .map(client => {
                      const clientTransactions = transactions.filter(t => t.clientId === client._id);
                      const earned = clientTransactions.filter(t => t.type === 'earned')
                        .reduce((sum, t) => sum + Math.abs(t.points), 0);
                      const spent = clientTransactions.filter(t => t.type === 'spent')
                        .reduce((sum, t) => sum + Math.abs(t.points), 0);

                      return (
                        <div key={client._id} className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <div>
                              <strong>{client.nom || client.email}</strong>
                              <div className="small text-muted">{client.email}</div>
                            </div>
                            <div className="text-end">
                              <Badge bg={
                                client.level === 'Platinum' ? 'dark' :
                                  client.level === 'Gold' ? 'warning' :
                                    client.level === 'Silver' ? 'secondary' : 'light'
                              } text={client.level === 'Bronze' ? 'dark' : 'light'}>
                                {client.level || 'Bronze'}
                              </Badge>
                              <div className="fw-bold text-primary mt-1">{client.points || 0} pts</div>
                            </div>
                          </div>
                          <div className="d-flex justify-content-between small text-muted">
                            <span>Gagnés: +{earned}</span>
                            <span>Dépensés: -{spent}</span>
                            <span>Transactions: {clientTransactions.length}</span>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default PointsReport;